import React, { useMemo } from "react";

import clsx from "clsx";

import { useAdoptedBudgetMix } from "@features/mmm/hooks/use-adopted-budget-mix";
import { TrackPerformanceTrackedComponent } from "@pages/mmm/amplitude-tracking/components";
import {
  getMMMEventSelectionData,
  MMMTab,
} from "@pages/mmm/amplitude-tracking/mmm-event";
import { useTrackMMMEvent } from "@pages/mmm/amplitude-tracking/use-track-mmm-event";
import { useClientKpis } from "@pages/mmm/hooks/use-client-kpis";
import { useClientSettingData } from "@pages/mmm/hooks/use-client-setting-data";
import { useClientSettings } from "@pages/mmm/hooks/use-client-settings";
import { FlatSelect, type FlatSelectOption } from "@shared/flat-select";

import { usePerformanceTrackingChartMetric } from "../hooks/use-chart-state";

const optionRender =
  (label: string) =>
  ({ isSelected }: { isSelected: boolean }) =>
    (
      <div
        className={clsx("px-2 flex-center h-full", {
          "text-white font-semibold": isSelected,
        })}
      >
        {label}
      </div>
    );

export function MetricSelector() {
  const trackMMMEvent = useTrackMMMEvent(MMMTab.TRACK_PERFORMANCE);
  const [metric, setMetric] = usePerformanceTrackingChartMetric();
  const { adoptedClientSetting } = useClientSettings();
  const { adoptedBudgetMix } = useAdoptedBudgetMix();
  const { kpis: clientKpis } = useClientKpis();
  const { kpi: adoptedClientSettingKpi } =
    useClientSettingData(adoptedClientSetting);

  const options = useMemo(() => {
    let kpiName = adoptedClientSettingKpi?.friendly_name;

    if (!kpiName) {
      kpiName = clientKpis.find(
        (e) => e.kpi_name === adoptedBudgetMix?.data.kpi_name,
      )?.friendly_name;
    }

    return [
      {
        label: "Spend",
        value: "spend",
        onClick: () => setMetric("spend"),
        renderOption: optionRender("Spend"),
      },
      {
        label: kpiName,
        value: "kpi",
        onClick: () => setMetric("kpi"),
        renderOption: optionRender(kpiName ?? "[KPI]"),
      },
    ] as FlatSelectOption<string>[];
  }, [
    setMetric,
    adoptedClientSettingKpi?.friendly_name,
    clientKpis,
    adoptedBudgetMix?.data.kpi_name,
  ]);

  const selected = useMemo(() => {
    const option = options.find(({ value }) => value === metric);
    if (option) {
      return option;
    }
    return options[0];
  }, [metric, options]);

  const setSelected = (option: FlatSelectOption<string>) =>
    setMetric(option.value);

  return (
    <div>
      <div className="mb-1 text-xs font-medium text-interstellar+2 mr-2 tracking-wide">
        METRIC
      </div>
      <FlatSelect
        options={options}
        setSelected={(value) => {
          trackMMMEvent({
            component: TrackPerformanceTrackedComponent.METRIC_SELECTOR,
            action: "onSelect",
            data: getMMMEventSelectionData(value.label),
          });

          setSelected(value);
        }}
        selected={selected}
      />
    </div>
  );
}
